import React, { useEffect } from 'react'
import {
  Typography,
  RadioGroup,
  FormControlLabel,
  withStyles,
  MenuItem,
  Select,
  FormControl,
} from '@material-ui/core'
import Radio from '@material-ui/core/Radio'
import {
  CoreIconButton,
  CoreTextField,
  CoreAutoComplete,
} from 'src/components/core'
import { Icon } from '@iconify/react'
import styles from './styles'
import { hasYoutubeAttachment } from '../../utils/attachment'

const StyledRadio = withStyles({
  root: {
    padding: 0,
    marginRight: '6px',
    '&$checked': {
      padding: 0,
      marginRight: '6px',
      color: '#015FFB',
    },
  },
  checked: {
    padding: 0,
    marginRight: '6px',
  },
})(props => <Radio color="default" {...props} />)

const ImageSettings = ({
  classes,
  category,
  selectedSettings,
  onChangeHandler,
  removeItem,
  attachments = [],
}) => {
  const getCurrentValue = key => {
    const setting = selectedSettings.find(s => s.key === key)
    if (setting) {
      return setting.value
    }

    // If no setting found, return the default value from the item
    const item = category.settingItems.find(i => i.key === key)
    return item?.defaultValue?.value || null
  }

  const handleChange = (key, value) => {
    onChangeHandler({ key }, value)
  }

  const currentImageSource = getCurrentValue('imageSource')
  const shouldShowImageSettings =
    currentImageSource && currentImageSource === 'imageGen'

  const renderSettingItem = item => {
    if (item.key === 'imageSource') {
      let optionsList = [...item.list]
      const hasYoutube = hasYoutubeAttachment(attachments)

      if (hasYoutube) {
        optionsList.splice(1, 0, { name: 'Source', value: 'source' })
      }

      return (
        <div key={item.key} className={classes.imageSourceSection}>
          <RadioGroup
            value={currentImageSource}
            onChange={e => handleChange(item.key, e.target.value)}
            className={classes.radioGroup}
          >
            {optionsList.map(option => {
              const isDisabled =
                option.value === 'source' && attachments.length === 0

              return (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<StyledRadio />}
                  label={option.name}
                  disabled={isDisabled}
                  className={`${classes.radioLabel} ${
                    currentImageSource === option.value
                      ? classes.selectedRadioLabel
                      : ''
                  }`}
                />
              )
            })}
          </RadioGroup>
        </div>
      )
    }

    if (item.key === 'imageCharacter') {
      return (
        <div key={item.key} className={classes.imageSettingField}>
          <Typography className={classes.settingFieldDescription}>
            {item.description}
          </Typography>
          <CoreTextField
            marginTop={false}
            multiline
            value={getCurrentValue(item.key) || item.defaultValue?.value || ''}
            onChange={e => handleChange(item.key, e.target.value)}
            className={classes.textField}
            variant="outlined"
            placeholder="Add extra image keywords."
          />
        </div>
      )
    }

    if (item.key === 'imageStyle') {
      return (
        <div key={item.key} className={classes.imageSettingField}>
          <Typography className={classes.settingFieldDescription}>
            {item.description}
          </Typography>
          <FormControl
            fullWidth
            variant="outlined"
            className={classes.selectField}
          >
            <Select
              value={
                getCurrentValue(item.key) || item.defaultValue?.value || ''
              }
              onChange={e => handleChange(item.key, e.target.value)}
              displayEmpty
              className={classes.select}
            >
              {item.list.map(option => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  className={classes.menuItem}
                >
                  <div className={classes.menuItemContent}>
                    <span>{option.name}</span>
                  </div>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )
    }

    // For other dropdown fields
    return (
      <div key={item.key} className={classes.imageSettingField}>
        <Typography className={classes.settingFieldDescription}>
          {item.description}
        </Typography>
        <div className={classes.auto_complete}>
          <CoreAutoComplete
            marginTop={false}
            title={item.description}
            defaultValue={item.defaultValue}
            freeSolo={false}
            selectOnFocus
            options={item.list}
            disableClearable={true}
            onChange={(event, value) => {
              handleChange(
                item.key,
                value != null ? value.value : event.target.value,
              )
            }}
            onBlur={(event, value) => {
              if (
                item.list.find(x => x.name == event.target.value) == undefined
              ) {
                handleChange(
                  item.key,
                  value != null ? value.value : event.target.value,
                )
              }
            }}
            id={'auto_complete_' + item.key}
            key={'auto_complete_' + item.key}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={classes.imageSettingsContainer}>
      <div className={classes.imageSettingsHeader}>
        <div className={classes.imageSettingsHeaderTitleContainer}>
          <Typography className={classes.imageSettingsTitle}>
            {category.title}
          </Typography>
          <CoreIconButton
            color="dark"
            size="small"
            variant="text"
            onClick={() =>
              removeItem({
                key: category.type,
                removeItems: category.settingItems.map(item => item.key),
              })
            }
            className={classes.delete_button}
          >
            <Icon icon="iconamoon:close-light" />
          </CoreIconButton>
        </div>
      </div>

      <Typography className={classes.imageSettingsDescription}>
        {category.description}
      </Typography>

      <div className={classes.imageSettingsContent}>
        {category.settingItems.map(item => {
          // Hide image-related settings if imageSource is 'none'
          if (!shouldShowImageSettings && item.key !== 'imageSource') {
            return null
          }

          if (item.hidden) {
            return null
          }

          return renderSettingItem(item)
        })}
      </div>
    </div>
  )
}

export default withStyles(styles)(ImageSettings)
